import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import NotificationPanel from "../NotificationPanel/NotificationPanel";
import soundVistaLogo from "../../assets/images/soundvista.svg";
import "./header.scss";

function getTopbarDisplayName(name) {
  const value = (name || '').trim();
  if (!value) return 'Account';

  const parts = value.split(/\s+/);
  const first = parts[0];
  const lastInitial = parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : '';

  return lastInitial ? `${first} ${lastInitial}` : first;
}

export default function Header({ isNotifications = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const { unreadCount } = useNotifications();
  const isLobby = routeLocation.pathname === '/lobby';
  const useCompactShell = !isNotifications;
  const showShellBrand = isNotifications;
  const showBack = !isLobby && !isNotifications;
  const displayName = getTopbarDisplayName(user?.displayName);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const menuRef = useRef(null);
  const notifButtonRef = useRef(null);
  const closeNotifPanel = useCallback(() => setNotifPanelOpen(false), []);
  const handleNotificationClick = () => {
    if (isNotifications) {
      return;
    }

    setMenuOpen(false);
    setNotifPanelOpen((prev) => !prev);
  };

  const handleProfileClick = () => {
    setNotifPanelOpen(false);
    setMenuOpen((prev) => !prev);
  };

  // Close menu on click outside
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [routeLocation.pathname]);

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await logout();
      navigate('/signin', { replace: true });
    } catch (err) {
      // silent
    }
  };

  const renderTopbar = () => (
    <div className={`topbar${useCompactShell ? " topbar--lobby" : ""}${isNotifications ? " topbar--notifications" : ""}`}>
      <div className="topbar__icons">
        <button className="sv-icon-button topbar__icon-btn" type="button" onClick={() => navigate('/search')} aria-label="Search">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M21 20.9999L16.66 16.6599M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <button
          ref={notifButtonRef}
          className={`sv-icon-button topbar__icon-btn topbar__icon-btn--bell${notifPanelOpen || isNotifications ? " topbar__icon-btn--active" : ""}`}
          type="button"
          onClick={handleNotificationClick}
          aria-label={isNotifications ? "Notifications tray active" : "Notifications"}
          aria-pressed={notifPanelOpen || isNotifications}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9Z" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {unreadCount > 0 && <span className="topbar__dot" />}
        </button>
      </div>

      <div className="topbar__sep" />

      <button
        className={`topbar__profile${menuOpen ? " topbar__profile--active" : ""}`}
        type="button"
        onClick={handleProfileClick}
        aria-label="Profile menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <span className="topbar__name">{displayName}</span>
        {user?.photoURL ? (
          <img className="topbar__avatar" src={user.photoURL} alt="" width="40" height="40" referrerPolicy="no-referrer" />
        ) : (
          <div className="topbar__avatar topbar__avatar--placeholder">
            {displayName.charAt(0).toUpperCase()}
          </div>
        )}
      </button>

      {menuOpen && (
        <div className="topbar__menu" role="menu" aria-label="Profile menu">
          <div className="topbar__menu-items">
            <button className="topbar__menu-item topbar__menu-item--highlight" role="menuitem" type="button" onClick={() => { setMenuOpen(false); navigate('/profile'); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M20 21c0-3.31-3.58-6-8-6s-8 2.69-8 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
              <span>Profile</span>
            </button>
            <button className="topbar__menu-item" role="menuitem" type="button" onClick={() => { setMenuOpen(false); navigate('/settings'); }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" stroke="currentColor" strokeWidth="1.3"/>
              </svg>
              <span>Settings</span>
            </button>
            <button className="topbar__menu-item" role="menuitem" type="button" onClick={() => setMenuOpen(false)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                <circle cx="12" cy="17" r="0.5" fill="currentColor"/>
              </svg>
              <span>Help Center</span>
            </button>
          </div>
          <div className="topbar__menu-sep" />
          <button className="topbar__menu-item" role="menuitem" type="button" onClick={handleLogout}>
            <span>Log out</span>
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {showBack && (
        <button className="sv-icon-button sv-icon-button--back header-back" type="button" onClick={() => navigate(-1)} aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}

      {showShellBrand && (
        <button className="header-brand" type="button" onClick={() => navigate('/lobby')} aria-label="Go to lobby">
          <img src={soundVistaLogo} alt="SoundVista" className="header-brand__logo" />
        </button>
      )}

      <div ref={menuRef}>
        {renderTopbar()}
      </div>

      {!isNotifications && <NotificationPanel open={notifPanelOpen} onClose={closeNotifPanel} anchorRef={notifButtonRef} />}
    </>
  );
}
